import {
  publishEvent,
  pushDataLayer,
  refreshCart
} from "./chunk--chunk-GKVQSYZM.vbt.js";
import {
  UTM_KEYS,
  captureUtmParams,
  clearUtmCookies,
  getUtmProperties,
  isImpactLocked
} from "./chunk--chunk-Y4QYEKFN.vbt.js";
import {
  deleteCookie,
  getCookie,
  setCookie
} from "./chunk--chunk-7LFSMYBN.vbt.js";

// src/js/helpers/ComponentConstructor.js
function initComponentOnConnectedComponentLoad(componentToInit, handleToWatch, selectorDispatch, element, dataSelect) {
  document.addEventListener(handleToWatch + " loaded", function handler() {
    const component = new componentToInit(element, dataSelect);
    if (component.init) component.init();
    document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
    this.removeEventListener(handleToWatch + " loaded", handler);
  });
}
function initComponentOnConnectedComponentDomEvent(componentToInit, handleToWatch, trigger, selectorDispatch, element, dataSelect) {
  document.querySelectorAll("[data-" + handleToWatch + "]").forEach(
    (elementTrigger) => elementTrigger.addEventListener(trigger, function handler() {
      const component = new componentToInit(element, dataSelect);
      if (component.init) component.init();
      document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
      this.removeEventListener(trigger, handler);
    })
  );
}
function initComponentOnSelfDomEvent(componentToInit, trigger, selectorDispatch, element, dataSelect) {
  if (window.innerWidth < 768 && trigger === "mouseover") {
    initComponentOnSelfIntersection(
      componentToInit,
      selectorDispatch,
      element,
      dataSelect
    );
  } else {
    element.addEventListener(trigger, function handler() {
      const component = new componentToInit(element, dataSelect);
      if (component.init) component.init();
      document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
      this.removeEventListener(trigger, handler);
    });
  }
}
function initComponentOnSelfIntersection(componentToInit, selectorDispatch, element, dataSelect) {
  const component = new componentToInit(element, dataSelect);
  const runInit = () => {
    if (element.hasAttribute("data-component-initialized")) return;
    if (component.init) component.init();
    element.setAttribute("data-component-initialized", selectorDispatch);
    document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
  };
  if (component.init) {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        runInit();
        observer.disconnect();
      }
    }, {
      rootMargin: "300px 0px 0px 0px"
    });
    observer.observe(element);
    return;
  }
  runInit();
}
function initImmediateComponent(Component, selector, element, dataSelect) {
  const component = new Component(element, dataSelect);
  if (component.init) component.init();
  element.setAttribute("data-component-initialized", selector);
  document.dispatchEvent(new CustomEvent(selector + " loaded"));
}
function ComponentConstructor(components) {
  Object.entries(components).forEach(([selector, Component]) => {
    const dataSelect = "data-" + selector;
    const matchedElements = document.querySelectorAll("[" + dataSelect + "]");
    matchedElements.forEach((element) => {
      if (element.hasAttribute("data-component-initialized")) return;
      if (Array.isArray(Component)) {
        if (Component.filter((option) => typeof option === "string" && option.includes("url:")).length > 0) {
          const urls = Component.map((option) => typeof option === "string" && option.includes("url:") ? option.split("url:")[1] : false);
          let shouldInit = false;
          for (const url of urls) {
            if (url && window.location.href.includes(url)) {
              shouldInit = true;
            }
          }
          if (shouldInit) {
            initComponentOnSelfIntersection(
              Component[0],
              selector,
              element,
              dataSelect
            );
            return;
          }
        }
        Component.forEach((option, i) => {
          if (i === 0 || option.includes("url:")) return;
          if (option.includes(":")) {
            const triggerType = option.split(":")[0];
            const triggerSelector = option.split(":")[1];
            if (triggerType === "load") {
              initComponentOnConnectedComponentLoad(
                Component[0],
                triggerSelector,
                selector,
                element,
                dataSelect
              );
            } else {
              initComponentOnConnectedComponentDomEvent(
                Component[0],
                triggerSelector,
                triggerType,
                selector,
                element,
                dataSelect
              );
            }
          } else {
            initComponentOnSelfDomEvent(
              Component[0],
              option,
              selector,
              element,
              dataSelect
            );
          }
        });
        return;
      }
      if (element.hasAttribute("data-slider-immediate") || element.hasAttribute(`${dataSelect}-immediate`)) {
        initImmediateComponent(Component, selector, element, dataSelect);
        return;
      }
      initComponentOnSelfIntersection(Component, selector, element, dataSelect);
    });
  });
}

// src/js/helpers/pdpGalleryThumbSize.js
var PDP_GALLERY_THUMB_GAP = 8;
function getThumbCount(gallery) {
  return Number.parseInt(
    getComputedStyle(gallery).getPropertyValue("--pdp-gallery-thumb-count").trim() || "6",
    10
  );
}
function applyPdpGalleryThumbSizes(root) {
  const scope = root?.querySelectorAll ? root : document;
  const isDesktop = window.matchMedia("(min-width: 976px)").matches;
  scope.querySelectorAll("[data-pdp-media] .pdp-gallery").forEach((gallery) => {
    if (!isDesktop) {
      gallery.style.removeProperty("--pdp-gallery-thumb-size");
      gallery.removeAttribute("data-pdp-thumb-sized");
      return;
    }
    const galleryWidth = gallery.getBoundingClientRect().width;
    if (galleryWidth <= 0) return;
    const count = getThumbCount(gallery);
    const thumbSize = (galleryWidth - count * PDP_GALLERY_THUMB_GAP) / (count + 1);
    if (thumbSize <= 0) return;
    const next = `${thumbSize}px`;
    const current = gallery.style.getPropertyValue("--pdp-gallery-thumb-size");
    if (current === next) return;
    gallery.style.setProperty("--pdp-gallery-thumb-size", next);
    gallery.dataset.pdpThumbSized = "true";
  });
}
var resizeTimer;
function initPdpGalleryThumbSizing() {
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(applyPdpGalleryThumbSizes, 150);
  }, { passive: true });
  const desktopQuery = window.matchMedia("(min-width: 976px)");
  desktopQuery.addEventListener("change", () => applyPdpGalleryThumbSizes());
}
function applyPdpGalleryThumbSizeFromWidth(gallery, count, gapTotal, divisor) {
  if (!gallery || !window.matchMedia("(min-width: 976px)").matches) return false;
  const galleryWidth = gallery.offsetWidth;
  if (galleryWidth <= 0) return false;
  const thumbSize = (galleryWidth - gapTotal) / divisor;
  if (thumbSize <= 0) return false;
  gallery.style.setProperty("--pdp-gallery-thumb-size", `${thumbSize}px`);
  gallery.dataset.pdpThumbSized = "true";
  return true;
}
if (typeof window !== "undefined") {
  window.__issaApplyPdpGalleryThumbSize = applyPdpGalleryThumbSizeFromWidth;
}

// src/js/utils/addonEvents.js
var VIEW_ADDON_OFFER_EVENT = "view_addon_offer";
var ADD_ADDON_TO_CART_EVENT = "add_addon_to_cart";
var REMOVE_ADDON_FROM_CART_EVENT = "remove_addon_from_cart";
var ADDON_ENROLL_TRIGGER_EVENT = "addon:enroll-trigger";
var ADDON_ARM_RESOLVED_EVENT = "addon:arm-resolved";
var REMOVAL_USER = "user";
var REMOVAL_BASE_REMOVED = "base_removed";
var REMOVAL_SYSTEM_CORRECTION = "system_correction";
function toMajorUnits(cents) {
  return typeof cents === "number" ? cents / 100 : null;
}
function parseEntries(rawValues) {
  const entries = /* @__PURE__ */ new Map();
  rawValues.forEach((raw) => {
    try {
      const entry = JSON.parse(raw);
      if (entry && entry.key) entries.set(entry.key, entry);
    } catch (err) {
    }
  });
  return entries;
}
function diffLines(prev, next) {
  return {
    added: [...next.values()].filter((line2) => !prev.has(line2.key)),
    removed: [...prev.values()].filter((line2) => !next.has(line2.key))
  };
}
function parentOf(line2) {
  return line2.key.split(":")[1] || "";
}
function isLinked(line2) {
  return parentOf(line2) !== "";
}
function classifyRemoval(line2, orphanKeys, presentVariantIds) {
  if (orphanKeys.includes(line2.key)) return REMOVAL_SYSTEM_CORRECTION;
  if (!isLinked(line2)) return REMOVAL_USER;
  if (!presentVariantIds.has(Number(parentOf(line2)))) return REMOVAL_BASE_REMOVED;
  return REMOVAL_USER;
}
function offersToView(shown, offers, presentKeys) {
  const isPresent = (key) => presentKeys === void 0 || presentKeys.has(key);
  const present = [...offers.values()].filter((offer) => isPresent(offer.key));
  return {
    fire: present.filter((offer) => !shown.has(offer.key)),
    shown: new Set(present.map((offer) => offer.key))
  };
}
function basePayload(entry) {
  return {
    addon_id: entry.addon_id ?? null,
    addon_name: entry.addon_name ?? null,
    base_product_id: entry.base_product_id ?? null,
    base_product_name: entry.base_product_name ?? null,
    addon_price: toMajorUnits(entry.addon_price),
    payment_plan: entry.payment_plan ?? null,
    template_suffix: entry.template_suffix ?? null
  };
}
function buildViewPayload(offer) {
  return basePayload(offer);
}
function buildAddPayload(line2) {
  return basePayload(line2);
}
function buildRemovePayload(line2, removalType) {
  return { ...basePayload(line2), removal_type: removalType };
}
function shouldEnroll(alreadyTriggered, isOpen, presentOfferCount) {
  return !alreadyTriggered && isOpen === true && presentOfferCount > 0;
}
function shouldFireView(isOpen, armResolved) {
  return isOpen === true && armResolved === true;
}

// src/js/helpers/addonTracking.js
var enrollTriggered = false;
function emit(name, payload) {
  publishEvent(name, payload);
  pushDataLayer(name, payload);
}
function readState(drawer) {
  const attrs = (selector, attr) => [...drawer.querySelectorAll(selector)].map((el) => el.getAttribute(attr));
  return {
    offers: parseEntries(attrs("[data-cart-addon-offer]", "data-cart-addon-offer")),
    lines: parseEntries(attrs("[data-addon-line]", "data-addon-line")),
    variantIds: new Set(attrs("[data-cart-line-variant]", "data-cart-line-variant").map(Number))
  };
}
function presentOfferKeys(drawer) {
  return new Set(
    parseEntries(
      [...drawer.querySelectorAll("[data-cart-addon-offer]")].map(
        (el) => el.getAttribute("data-cart-addon-offer")
      )
    ).keys()
  );
}
function initAddonTracking(drawer) {
  try {
    let state = readState(drawer);
    let shown = /* @__PURE__ */ new Set();
    let open = false;
    let armResolved = false;
    const viewOffers = () => {
      const result = offersToView(shown, state.offers, presentOfferKeys(drawer));
      shown = result.shown;
      result.fire.forEach((offer) => emit(VIEW_ADDON_OFFER_EVENT, buildViewPayload(offer)));
    };
    document.addEventListener(ADDON_ARM_RESOLVED_EVENT, () => {
      try {
        armResolved = true;
        if (open) viewOffers();
      } catch (err) {
        console.error("[addon-tracking] arm-resolved view failed", err);
      }
    });
    if ("IntersectionObserver" in window) {
      new IntersectionObserver((entries) => {
        try {
          const entry = entries[entries.length - 1];
          const isOpen = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          if (isOpen === open) return;
          open = isOpen;
          if (open) {
            if (shouldFireView(open, armResolved)) viewOffers();
            if (shouldEnroll(enrollTriggered, open, presentOfferKeys(drawer).size)) {
              enrollTriggered = true;
              document.dispatchEvent(new CustomEvent(ADDON_ENROLL_TRIGGER_EVENT));
            }
          } else shown = /* @__PURE__ */ new Set();
        } catch (err) {
          console.error("[addon-tracking] view tracking failed", err);
        }
      }, { threshold: 0.5 }).observe(drawer);
    }
    document.addEventListener("ajaxcart refresh", (e) => {
      try {
        const next = readState(drawer);
        const orphanKeys = e.detail?.orphanedAddonKeys || [];
        const { added, removed } = diffLines(state.lines, next.lines);
        added.filter(isLinked).forEach((line2) => emit(ADD_ADDON_TO_CART_EVENT, buildAddPayload(line2)));
        removed.forEach((line2) => {
          const removalType = classifyRemoval(line2, orphanKeys, next.variantIds);
          emit(REMOVE_ADDON_FROM_CART_EVENT, buildRemovePayload(line2, removalType));
        });
        state = next;
      } catch (err) {
        console.error("[addon-tracking] cart diff failed", err);
      }
    });
  } catch (err) {
    console.error("[addon-tracking] init failed", err);
  }
}

// src/js/helpers/addonPriceDisplay.js
var PAY_IN_FULL = "PiF";
var fmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
function line(text, classNames) {
  const span = document.createElement("span");
  span.className = classNames;
  span.textContent = text;
  return span;
}
function selectAddonDisplay(data) {
  const today = Number(data.today);
  if (!Number.isFinite(today) || today <= 0) return null;
  const isPif = data.planType === PAY_IN_FULL;
  const monthly = Number(data.monthly);
  const hasOngoing = Number.isFinite(monthly) && monthly > 0;
  const remainingMonths = Number(data.remainingMonths) || 0;
  const showThen = !isPif && hasOngoing && remainingMonths > 0;
  return { today, monthly, remainingMonths, isPif, showThen };
}
function renderPriceBlock(container) {
  let data;
  try {
    data = JSON.parse(container.getAttribute("data-addon-price-plan") || "{}");
  } catch (err) {
    console.error("[addon-price-display] unreadable data-addon-price-plan", err);
    return;
  }
  const display = selectAddonDisplay(data);
  if (!display) return;
  container.textContent = "";
  if (display.isPif) {
    container.appendChild(line(fmt.format(display.today), "font-primary text-h4 leading-7 font-medium text-neutral-900"));
    container.appendChild(line("one-time purchase", "font-special text-p leading-6 font-normal text-gray-dark"));
    return;
  }
  const todayRow = document.createElement("span");
  todayRow.className = "flex items-baseline gap-1";
  todayRow.appendChild(line(fmt.format(display.today), "font-primary text-h4 leading-7 font-medium text-neutral-900"));
  todayRow.appendChild(line("today", "font-special text-p leading-6 font-normal text-neutral-900"));
  container.appendChild(todayRow);
  if (display.showThen) {
    container.appendChild(line(
      `then ${display.remainingMonths} mos. @ ${fmt.format(display.monthly)}`,
      "font-special text-p leading-6 font-normal text-gray-dark"
    ));
  }
}
function renderAddonPriceDisplays(root) {
  root.querySelectorAll("[data-addon-price-display]").forEach(renderPriceBlock);
}
function initAddonPriceDisplay(drawer) {
  renderAddonPriceDisplays(drawer);
  document.addEventListener("ajaxcart refresh", () => renderAddonPriceDisplays(drawer));
}

// src/js/utils/addonAddRequest.js
var SECTIONS = "ajaxcart";
var SECTIONS_URL = "/";
var NUMERIC_PARAMS = ["id", "quantity", "selling_plan"];
function addonAddRequest(href) {
  const url = new URL(href);
  const item = { properties: {} };
  url.searchParams.forEach((value, name) => {
    const property = name.match(/^properties\[(.+)\]$/);
    if (property) item.properties[property[1]] = value;
    else if (NUMERIC_PARAMS.includes(name) && value !== "" && Number.isFinite(Number(value))) item[name] = Number(value);
  });
  return {
    addUrl: `${url.pathname}.js`,
    body: { items: [item], sections: SECTIONS, sections_url: SECTIONS_URL }
  };
}

// src/js/components/AjaxCart.js
var AjaxCart = class {
  constructor(el) {
    this.el = el;
    this.errEls = el.querySelectorAll("[data-err]");
    const requestButtons = el.querySelectorAll("[data-ajax-cart-request-button]");
    this.emptyCartContent = el.querySelectorAll("[data-empty-cart-content]");
    this.cartWithItemsContent = el.querySelectorAll("[data-cart-with-items-content]");
    requestButtons.forEach((button) => {
      button.addEventListener("click", (e) => this.buttonListener(e));
    });
    document.addEventListener("ajaxcart refresh", () => {
      this.toggleContent();
      const requestButtons2 = document.querySelectorAll("[data-ajax-cart-request-button]");
      requestButtons2.forEach((button) => {
        button.addEventListener("click", (e) => this.buttonListener(e));
      });
    });
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("open_cart")) {
      const cartToggle = document.getElementById("cart-drawer-toggle");
      if (cartToggle) {
        cartToggle.checked = true;
      }
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
    this.bindSwipeToClose(el);
    initAddonTracking(el);
    initAddonPriceDisplay(el);
    this.bindCheckoutSweep(el);
    this.bindDrawerOpenReconcile(el);
    if (el.querySelector("[data-addon-orphan]")) {
      refreshCart().catch((err) => console.error("[ajaxcart] orphan sweep failed", err));
    }
  }
  // Reconcile when the drawer is opened, not only at page load. A base course removed via a
  // path that does not re-render the drawer (a /cart/change.js call, another tab) leaves the
  // page's drawer markup stale, so its orphan/mismatch flags are from before the change and the
  // load-time sweep above cannot see the new orphan. One refreshCart on open re-fetches the
  // fragment (re-computing the flags against the live cart) and runs the sweep, within the
  // single-request model. refreshCart is queued, so an open during an in-flight refresh is safe.
  bindDrawerOpenReconcile(drawer) {
    const toggle = document.getElementById("cart-drawer-toggle");
    if (!toggle) return;
    toggle.addEventListener("change", () => {
      if (!toggle.checked) return;
      refreshCart().catch((err) => console.error("[ajaxcart] drawer-open reconcile failed", err));
    });
  }
  // DEV-3437: the drawer can be stale if the cart changed in another tab, so clear orphaned
  // add-ons right before checkout. Fail-open: an error or a slow cart never blocks checkout.
  bindCheckoutSweep(drawer) {
    const form = drawer.querySelector("[data-ajaxcart-checkout]");
    if (!form) return;
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) form.removeAttribute("data-pending");
    });
    form.addEventListener("submit", async (e) => {
      if (!drawer.querySelector("[data-addon-line]")) return;
      e.preventDefault();
      if (form.hasAttribute("data-pending")) return;
      form.setAttribute("data-pending", "");
      let result;
      try {
        result = await Promise.race([refreshCart(), new Promise((resolve) => setTimeout(resolve, 1500))]);
      } catch (err) {
        console.error("[ajaxcart] checkout orphan sweep failed", err);
      }
      if (result?.itemCount === 0) {
        form.removeAttribute("data-pending");
        return;
      }
      form.submit();
    });
  }
  bindSwipeToClose(drawer) {
    let startX = 0;
    let startY = 0;
    let tracking = false;
    drawer.addEventListener("touchstart", (e) => {
      if (window.innerWidth >= 1024) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });
    drawer.addEventListener("touchend", (e) => {
      if (!tracking) return;
      tracking = false;
      const deltaX = e.changedTouches[0].clientX - startX;
      const deltaY = e.changedTouches[0].clientY - startY;
      if (deltaX > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
        const toggle = document.getElementById("cart-drawer-toggle");
        if (toggle) toggle.checked = false;
      }
    }, { passive: true });
  }
  async buttonListener(e) {
    e.preventDefault();
    e.stopPropagation();
    const anchor = e.target.tagName === "a" ? e.target : e.target.closest("a");
    const isAddonAdd = anchor.hasAttribute("data-addon-add");
    const addonTitle = anchor.dataset.addonTitle || "";
    if (isAddonAdd) {
      if (anchor.hasAttribute("data-pending")) return;
      anchor.setAttribute("data-pending", "");
    }
    try {
      if (isAddonAdd) {
        await this.addAddon(anchor, addonTitle);
        return;
      }
      const res = await fetch(anchor.href);
      if (res.status === 200) {
        await refreshCart();
        this.hideErrors();
      } else {
        const errText = await res.text();
        const div = document.createElement("div");
        div.innerHTML = errText;
        const message = div.querySelector("h3 ~ .content--desc").innerHTML.replaceAll("\n", "").split(":")[0];
        this.showError(message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isAddonAdd) anchor.removeAttribute("data-pending");
    }
  }
  // DEV-3445: one request adds the add-on and returns the re-rendered drawer (bundled section
  // rendering). Following the card's /cart/add link instead downloads the whole /cart page,
  // then the drawer is fetched again on its own.
  // The add runs inside refreshCart's queue, so a refresh already in flight (page-load orphan
  // sweep, discount) finishes first and this response is never rendered over a newer drawer.
  async addAddon(anchor, addonTitle) {
    const { addUrl, body } = addonAddRequest(anchor.href);
    const result = await refreshCart({
      addonTitle,
      load: async () => {
        const res = await fetch(addUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          const text = [data?.description, data?.message].find((m) => typeof m === "string" && m) || "";
          const message = text.split(":")[0];
          if (message) this.showError(escapeHtml(message));
          else console.error("[ajaxcart] add-on add failed", res.status);
          return false;
        }
        return data?.sections?.ajaxcart ?? null;
      }
    });
    if (result) this.hideErrors();
  }
  showError(message) {
    this.errEls.forEach((el) => {
      el.style.removeProperty("display");
      el.querySelector("[data-target]").innerHTML = message;
      applyErrVariant(el, message);
    });
  }
  hideErrors() {
    this.errEls.forEach((el) => {
      el.style.display = "none";
      el.querySelector("[data-target]").innerHTML = "";
    });
  }
  async toggleContent() {
    const res = await fetch("/cart.js");
    const cart = await res.json();
    if (cart.item_count === 0) {
      this.emptyCartContent.forEach((content) => content.classList.remove("hidden"));
      this.cartWithItemsContent.forEach((content) => content.classList.add("hidden"));
    } else {
      this.emptyCartContent.forEach((content) => content.classList.add("hidden"));
      this.cartWithItemsContent.forEach((content) => content.classList.remove("hidden"));
    }
  }
};
function escapeHtml(text) {
  const span = document.createElement("span");
  span.textContent = text;
  return span.innerHTML;
}
function applyErrVariant(el, message) {
  const keywords = (el.dataset.infoKeywords || "").split(",").map((k) => k.trim().toLowerCase()).filter(Boolean);
  const lower = (message || "").toLowerCase();
  const isInfo = keywords.some((k) => lower.includes(k));
  el.style.backgroundColor = isInfo ? el.dataset.infoBg : el.dataset.errBg;
  el.style.setProperty("--cart-icon-color", isInfo ? el.dataset.infoIcon : el.dataset.errIcon);
  el.querySelector("[data-icon-err]").classList.toggle("hidden", isInfo);
  el.querySelector("[data-icon-info]").classList.toggle("hidden", !isInfo);
}
var AjaxCart_default = AjaxCart;

// src/global.vbt.js
(async () => {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const hasUtmParams = Array.from(params.keys()).some(
    (key) => key.startsWith("utm_")
  );
  const thankYouPattern = /checkouts\/.*thank[-_]you/i;
  const isThankYouPage = thankYouPattern.test(window.location.href);
  const cameFromThankYou = thankYouPattern.test(document.referrer);
  const purchaseFlag = getCookie("_issa_purchased");
  if (isThankYouPage || cameFromThankYou || purchaseFlag) {
    if (purchaseFlag) deleteCookie("_issa_purchased");
    const postPurchaseUtm = getUtmProperties();
    const isImpact = postPurchaseUtm._utm_source && postPurchaseUtm._utm_source === "impact";
    if (!isImpact) {
      clearUtmCookies();
      deleteCookie("irclickid");
      deleteCookie("sharedid");
    }
  }
  if (!getCookie("_issa_ft")) setCookie("_issa_ft", url.href, 30);
  const wasImpactLocked = isImpactLocked();
  if (hasUtmParams) captureUtmParams(params);
  if (!wasImpactLocked) {
    const urlIrclickid = params.get("irclickid");
    if (urlIrclickid) setCookie("irclickid", urlIrclickid, 30);
    const urlSharedid = params.get("sharedid");
    if (urlSharedid) setCookie("sharedid", urlSharedid, 30);
  }
  const cart = await fetch("/cart.js").then((res) => res.json());
  const attributes = cart.attributes || {};
  const expiresTime = attributes.expires ? new Date(attributes.expires).getTime() : null;
  const now = Date.now();
  const isExpired = expiresTime && now > expiresTime;
  let updatedAttributes = null;
  if (hasUtmParams) {
    updatedAttributes = {
      page_url: url.href,
      expires: new Date(now + 24 * 60 * 60 * 1e3).toISOString()
    };
  } else if (isExpired) {
    updatedAttributes = { page_url: "", expires: "" };
  }
  const irclickid = getCookie("irclickid");
  const sharedid = getCookie("sharedid");
  if (irclickid) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._irclickid = irclickid;
    updatedAttributes._tracked_at = (/* @__PURE__ */ new Date()).toISOString();
  } else if (attributes._irclickid) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._irclickid = "";
    updatedAttributes._tracked_at = "";
  }
  if (sharedid) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._sharedid = sharedid;
  } else if (attributes._sharedid) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._sharedid = "";
  }
  const firstTouchUrl = getCookie("_issa_ft");
  if (firstTouchUrl) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._issa_ft = firstTouchUrl;
  } else if (attributes._issa_ft) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._issa_ft = "";
  }
  const issaUtmRaw = getCookie("_issa_utm");
  if (issaUtmRaw) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._issa_utm = issaUtmRaw;
  } else if (attributes._issa_utm) {
    updatedAttributes = updatedAttributes || {};
    updatedAttributes._issa_utm = "";
  }
  const utmProps = getUtmProperties();
  if (Object.keys(utmProps).length) {
    updatedAttributes = updatedAttributes || {};
    Object.assign(updatedAttributes, utmProps);
    updatedAttributes._tracked_at = updatedAttributes._tracked_at || (/* @__PURE__ */ new Date()).toISOString();
  } else if (UTM_KEYS.some((k) => attributes["_" + k])) {
    updatedAttributes = updatedAttributes || {};
    UTM_KEYS.forEach((k) => {
      updatedAttributes["_" + k] = "";
    });
  }
  if (updatedAttributes) {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: updatedAttributes })
    });
  }
})();
initPdpGalleryThumbSizing();
document.addEventListener(
  "DOMContentLoaded",
  async function globalComponents() {
    const components = {
      ajaxcart: AjaxCart_default
    };
    const [
      sliderModule,
      productFormModule,
      intersectorModule,
      affiliateBannerModule,
      spanishEncodingModule,
      smoothScrollModule,
      searchModule,
      articleAdvisorCtaModule,
      navMegaMenuModule
    ] = await Promise.all([
      document.querySelector("[data-slider]") ? import("./chunk--Slider-SH32QBCR.vbt.js") : null,
      document.querySelector("[data-product-form-immediate]") ? import("./chunk--ProductForm-WYI43XO6.vbt.js") : null,
      document.querySelector("[data-intersector-immediate]") ? import("./chunk--Intersector-2QYSAS6N.vbt.js") : null,
      document.querySelector("[data-affiliate-banner]") ? import("./chunk--AffiliateBanner-P4EMVTE6.vbt.js") : null,
      document.querySelector("[data-spanish-encoding-mojibake]") ? import("./chunk--spanishEncodingMojibake-MHMMUQC2.vbt.js") : null,
      document.querySelector("[data-smooth-scroll]") ? import("./chunk--SmoothScroll-5VGIKCZG.vbt.js") : null,
      document.querySelector("[data-search]") ? import("./chunk--Search-GPKHXWDC.vbt.js") : null,
      document.querySelector("[data-article-advisor-cta]") ? import("./chunk--ArticleAdvisorCta-Q7GIEPXE.vbt.js") : null,
      document.querySelector("[data-nav-primary-v2]") ? import("./chunk--NavMegaMenu-4POMORG3.vbt.js") : null
    ]);
    if (affiliateBannerModule) {
      components["affiliate-banner"] = affiliateBannerModule.default;
    }
    if (spanishEncodingModule) {
      spanishEncodingModule.default();
    }
    if (sliderModule) {
      components["slider"] = sliderModule.default;
    }
    if (productFormModule) {
      components["product-form"] = productFormModule.default;
    }
    if (intersectorModule) {
      components["intersector"] = intersectorModule.default;
    }
    if (smoothScrollModule) {
      components["smooth-scroll"] = smoothScrollModule.default;
    }
    if (searchModule) {
      components["search"] = searchModule.default;
    }
    if (articleAdvisorCtaModule) {
      components["article-advisor-cta"] = articleAdvisorCtaModule.default;
    }
    if (navMegaMenuModule) {
      components["nav-primary-v2"] = navMegaMenuModule.default;
    }
    ComponentConstructor(components);
    document.dispatchEvent(new CustomEvent("global components loaded"));
    this.removeEventListener("DOMContentLoaded", globalComponents);
  }
);
document.addEventListener(
  "global components loaded",
  async function asyncComponents() {
    const components = {};
    if (document.querySelector("[data-partners-page]")) {
      const PartnersPage = await import("./chunk--PartnersPage-AZQII3C3.vbt.js");
      components["partners-page"] = PartnersPage.default;
    }
    if (document.querySelector("[data-hc-control]")) {
      const Accordion = await import("./chunk--Accordion-LTW566WQ.vbt.js");
      components["hc-control"] = Accordion.default;
    }
    if (document.querySelector("[data-range]")) {
      const Range = await import("./chunk--Range-653A73ZZ.vbt.js");
      components["range"] = Range.default;
    }
    if (document.querySelector(
      "[data-intersector]:not([data-component-initialized])"
    )) {
      const Intersector = await import("./chunk--Intersector-2QYSAS6N.vbt.js");
      components["intersector"] = Intersector.default;
    }
    if (document.querySelector(
      "[data-product-form]:not([data-component-initialized])"
    )) {
      const ProductForm = await import("./chunk--ProductForm-WYI43XO6.vbt.js");
      components["product-form"] = ProductForm.default;
    }
    if (document.querySelector("[data-ajaxinate]")) {
      const Ajaxinate = await import("./chunk--Ajaxinate-QULWTO5A.vbt.js");
      components["ajaxinate"] = Ajaxinate.default;
    }
    if (document.querySelector("[data-header-search]")) {
      const HeaderSearch = await import("./chunk--HeaderSearch-EBN5TRRS.vbt.js");
      components["header-search"] = HeaderSearch.default;
    }
    if (document.querySelector("[data-discount]")) {
      const Discount = await import("./chunk--Discount-RN6OI7YT.vbt.js");
      components["discount"] = Discount.default;
    }
    if (document.querySelector("[data-bmi-calculator]")) {
      const BMICalculator = await import("./chunk--BMICalculator-VFE26U7F.vbt.js");
      components["bmi-calculator"] = BMICalculator.default;
    }
    if (document.querySelector("[data-advisor-quiz]")) {
      const AdvisorQuiz = await import("./chunk--AdvisorQuiz-VB3S3DMV.vbt.js");
      components["advisor-quiz"] = AdvisorQuiz.default;
    }
    if (document.querySelector("[data-cert-validation]")) {
      const CertificateValidation = await import("./chunk--CertificateValidation-QQEIPJRX.vbt.js");
      components["cert-validation"] = CertificateValidation.default;
    }
    if (document.querySelector("[data-reviews-slider]")) {
      const ReviewsSlider = await import("./chunk--Slider-SH32QBCR.vbt.js");
      components["reviews-slider"] = ReviewsSlider.default;
    }
    if (document.querySelector("[data-request-a-call]")) {
      const RequestACall = await import("./chunk--RequestACall-PMSRHGB4.vbt.js");
      components["request-a-call"] = RequestACall.default;
    }
    ComponentConstructor(components);
    document.dispatchEvent(new CustomEvent("async components loaded"));
    this.removeEventListener("global components loaded", asyncComponents);
  }
);
